import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import Carousel from "../components/Carousel/Carousel";
import Header from "../components/Header/Header";
import Button from "../components/Button/Button";
import '../css/Accueil.css';
import { useTranslation } from 'react-i18next';
import cvUrl from '../assets/CV_Brieuc_Joonnekindt_FR-EN.pdf';
import { fetchTechnologies, fetchProjects } from "../services/api";

const resolveIconPath = (icon) => (icon ? (icon.startsWith('/') ? icon : `/${icon}`) : null);

const resolveImagePath = (image) => (image ? (image.startsWith('/') ? image : `/${image}`) : null);

const fallbackCarouselImages = [
    "/assets/projets/alizon/index.png",
  "/assets/projets/samourail-malicieux/index.png",
  "/assets/projets/portfolio-communication/index.png"
];



function Accueil() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [technologyGroups, setTechnologyGroups] = useState([]);
  const [isLoadingTech, setIsLoadingTech] = useState(true);
  const [featuredProjects, setFeaturedProjects] = useState([]);

  useEffect(() => {
    let mounted = true;
    const loadTechnologies = async () => {
      try {
        const data = await fetchTechnologies();
        if (!mounted || !data) return;
        const grouped = data.categories.map((category) => ({
          ...category,
          items: data.items.filter((item) => item.categoryId === category.id)
        }));
        setTechnologyGroups(grouped);
      } catch (error) {
        console.error('Erreur lors du chargement des technologies', error);
        setTechnologyGroups([]);
      } finally {
        if (mounted) {
          setIsLoadingTech(false);
        }
      }
    };

    loadTechnologies();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    fetchProjects()
      .then((projects) => {
        if (!mounted || !projects) return;
        const featured = projects
          .filter((p) => p.homeCarouselPosition != null)
          .sort((a, b) => a.homeCarouselPosition - b.homeCarouselPosition);
        setFeaturedProjects(featured);
      })
      .catch((error) => console.error('Erreur lors du chargement des projets', error));
    return () => {
      mounted = false;
    };
  }, []);

  const carouselSlides = useMemo(() => {
    const slides = featuredProjects
      .map((project) => ({
        src: resolveImagePath(project.homeCoverImage || project.images?.[0] || project.logo),
        alt: project.nom?.[lang] || project.nom?.fr,
        href: `/projet/${project.seturl}`
      }))
      .filter((slide) => slide.src);
    return slides.length > 0 ? slides : fallbackCarouselImages;
  }, [featuredProjects, lang]);

  const downloadLabel = useMemo(() => t('home.downloadCV'), [t]);
  const seeProjectsLabel = useMemo(() => t('home.seeProjects'), [t]);

  const calculateAge = () => {
      const birthDate = new Date('2003-11-06'); // Mettez votre date de naissance réelle ici
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      
      return age;
    };

    return (
      <div>
      <Header title={t('home.headerTitle')}/>
      
      <div className="infos">
        <p>{t('home.intro', { age: calculateAge() })}</p>
        <a className="button" href={cvUrl} download>
          {downloadLabel}
        </a>
      </div>
      
      <motion.div 
            initial={{ opacity: 0, y: 50 }} // Animation commence en bas
            whileInView={{ opacity: 1, y: 0 }} // Animation lorsqu'il est visible
            viewport={{ once: true }} // Ne s'anime qu'une fois
            transition={{ duration: 0.3, ease: "easeIn" }} // Animation fluide
            className="projet-wrapper"
          >
        <div className="competences" id="competences">
          <img src="assets/black-line-return.png" alt="background" className="black-line bg-white" />
          <h1>{t('home.skillsTitle')}</h1>

          <div className="competences-lst">
          {isLoadingTech && <p>{t('home.loadingSkills')}</p>}
          {!isLoadingTech && technologyGroups.length === 0 && (
            <p>{t('home.noSkills')}</p>
          )}
          {technologyGroups.map((category) => (
            <div key={category.id} className="competences-row">
              <h2>{category.label[lang] || category.label.fr}</h2>
              <div className="competences-row-icons">
                {category.items.map((tech) => {
                  const label = tech.label[lang] || tech.label.fr;
                  return resolveIconPath(tech.icon) ? (
                    <img key={tech.id} src={resolveIconPath(tech.icon)} alt={label} title={label} />
                  ) : (
                    <div key={tech.id} className="competences-icon-placeholder" title={label} />
                  );
                })}
              </div>
            </div>
          ))}
          </div>
        </div>
      </motion.div>

      <img src="assets/black-line.png" alt="background" className="black-line" />

      <div className="projets">
        <h1>{t('home.projectsTitle')}</h1>

        <Carousel images={carouselSlides} className="carousel-container"/>
        <Button url="/projets" texte={seeProjectsLabel} />
      </div>
      </div>
    );
}

export default Accueil;